import type { PMIssueFilters } from "@/domains/pm/types";
import type { IssueFilterValue } from "@/domains/pm/components/issues/issueFilters";
import { overdueDueBefore } from "@/domains/pm/utils/dueDate";

/** Page scope the filter selection is applied within. */
export interface IssueFilterScope {
  buildingProjectId?: number;
  search?: string;
}

/**
 * The user's filter selection as the SERVER-side query the store runs.
 *
 * Status and overdue used to be header tabs; status narrowed the loaded page
 * client-side while Overdue narrowed the fetch. That asymmetry mattered: the
 * page is capped at PM_PAGE_SIZE_ISSUES rows ordered by issue_number, so an
 * overdue filter applied client-side silently loses every overdue issue past
 * the cap. Both are now filters, and BOTH go to the server for that reason.
 *
 * Overdue is the query form of isIssueOverdue: in_progress AND due before
 * today. It therefore PINS the status — an overdue-and-closed issue does not
 * exist — overriding whatever status is selected, so the two can never express
 * an empty intersection. The selection itself is left untouched so switching
 * overdue back off restores it; the control disables the ruled-out statuses
 * and drops the status chip while overdue is on.
 *
 * Kept pure and out of the page's effect so it can be tested directly: getting
 * it wrong loses rows silently rather than failing.
 */
export function toPMIssueFilters(value: IssueFilterValue, scope: IssueFilterScope): PMIssueFilters {
  const search = scope.search?.trim();
  return {
    buildingProjectId: scope.buildingProjectId,
    assigneeUserIds: value.assigneeUserIds.length ? value.assigneeUserIds : undefined,
    disciplineIds: value.disciplineIds.length ? value.disciplineIds : undefined,
    hasLinkedDocuments: value.hasLinkedDocuments,
    hasComments: value.hasComments,
    search: search || undefined,
    status: value.overdueOnly ? ["in_progress"] : value.status.length ? value.status : undefined,
    dueBefore: value.overdueOnly ? overdueDueBefore() : undefined,
  };
}

/**
 * The baseline the "N issues hidden by filters" count is measured against:
 * the page's scope with none of the filter dimensions applied.
 *
 * Status and overdue must be absent here. As tabs they were a *view*, so they
 * narrowed the baseline exactly as they narrowed the list and never counted as
 * hidden. As filters they hide issues like any other dimension, and the count
 * has to say so.
 */
export function toScopeBaselineFilters(scope: IssueFilterScope): PMIssueFilters {
  const search = scope.search?.trim();
  return {
    buildingProjectId: scope.buildingProjectId,
    search: search || undefined,
  };
}
